import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import axios from "../axios/axios";
import { RootState } from "./index";
import { videoConsultActions } from "./videoConsult-slice";
import { OnlineConsultPageResponse } from "../types/api";
import { isSuppressed } from "../utils/consultSuppression";

export interface FetchIncomingConsultsOptions {
  /** Aborted by the polling hook on disable/unmount. */
  signal?: AbortSignal;
  /**
   * Generation check from the polling hook: true once polling was disabled or
   * restarted after this request began. A stale settlement dispatches NO
   * Redux action — the request's generation no longer owns the state.
   */
  isStale?: () => boolean;
}

export const fetchIncomingConsults = (
  consultantId: number,
  options: FetchIncomingConsultsOptions = {}
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const { signal, isStale } = options;
    const stale = () => Boolean(signal?.aborted) || Boolean(isStale?.());

    try {
      dispatch(videoConsultActions.fetchIncomingConsultsStart());

      const params = {
        type: "PHARMACIST_SUPPORT",
        statusIn: "CREATED,IN_PROGRESS",
        consultantId: consultantId,
        orderBy: "created:desc",
      };

      const response = await axios.get<OnlineConsultPageResponse>(
        "/online_consult",
        { params, signal }
      );

      if (stale()) return;

      // Backend returns paginated response, but we expect only 0-1 active consultations
      const consultations = response.data.elements || [];

      // Drop consults this browser just joined (PiP): covers the seconds-wide
      // window between the local `joined-meeting` event and the backend
      // persisting the join webhook, after which the discovery predicate
      // excludes them server-side. READ-only — the thunk never writes
      // suppression storage (no reconciliation by design; see
      // consultSuppression.ts).
      const filtered = consultations.filter(
        (consult) => !isSuppressed(consult.id)
      );
      dispatch(videoConsultActions.fetchIncomingConsultsSuccess(filtered));
    } catch (error: any) {
      if (stale()) return;

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch incoming consultations";

      console.error("[VideoConsultActions] API call failed:", {
        error,
        errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      });

      dispatch(videoConsultActions.fetchIncomingConsultsFailed(errorMessage));
    }
  };
};

export const stopRingtone = (): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    dispatch(videoConsultActions.stopRingtone());
  };
};

export const clearConsultations = (): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    dispatch(videoConsultActions.clearIncomingConsults());
  };
};

export const rejectConsultation = (
    roomName: string,
    consultantId: number
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      await axios.delete("/online_consult/reject", {
        params: { roomName, consultantId },
      });

      dispatch(videoConsultActions.stopRingtone());
      dispatch(videoConsultActions.clearIncomingConsults());
    } catch (error: any) {
      const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to reject consultation";

      console.error("[VideoConsultActions] Reject failed:", {
        errorMessage,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      dispatch(videoConsultActions.stopRingtone());
    }
  };
};
